import React, {useState} from 'react';
import {Layout, Modal, Input, InputNumber, Drawer, Tag, Switch} from "antd";
import {Button as NButton, Card, Container, Text} from "@nextui-org/react";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, doc, getFirestore, query, setDoc, updateDoc, where, serverTimestamp} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";

const db = getFirestore(Firebase);
const auth = getAuth(Firebase);
const {Content} = Layout;

function fmtDate(val: any) {
    if (!val) return '—';
    const d = typeof val.toDate === 'function' ? val.toDate() : val;
    return moment(d).format('DD MMM YYYY, HH:mm');
}

const emptyForm = {code: '', ownerId: '', ownerName: '', target: undefined as number | undefined, rewardNote: ''};

// ── Create/edit modal ────────────────────────────────────────────────────────

function CodeFormModal({open, onClose, editing}: {open: boolean; onClose: () => void; editing: any | null}) {
    const toast = useToast();
    const [form, setForm] = useState(editing ?? emptyForm);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => { setForm(editing ?? emptyForm); }, [editing, open]);

    const submit = async () => {
        const code = form.code.trim().toUpperCase();
        if (!code || !form.ownerId.trim()) {
            toast({position: 'top', status: 'warning', title: 'Code and owner UID are required'});
            return;
        }
        setSubmitting(true);
        try {
            await setDoc(doc(db, 'referralCodes', code), {
                code,
                ownerId: form.ownerId.trim(),
                ownerName: form.ownerName.trim(),
                target: form.target ?? null,
                rewardNote: form.rewardNote.trim() || null,
                status: editing?.status ?? 'active',
                signupCount: editing?.signupCount ?? 0,
                createdAt: editing ? editing.createdAt : serverTimestamp(),
                createdBy: auth.currentUser?.uid ?? '',
            }, {merge: true});
            toast({position: 'top', status: 'success', title: editing ? 'Code updated' : 'Code created'});
            onClose();
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to save', description: e?.message});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} title={editing ? `Edit ${editing.code}` : 'New referral code'} onCancel={onClose} onOk={submit} confirmLoading={submitting} okText="Save">
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                <div>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Code</div>
                    <Input value={form.code} disabled={!!editing} placeholder="e.g. TIYA10"
                           onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} />
                </div>
                <div>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Owner user UID</div>
                    <Input value={form.ownerId} disabled={!!editing} placeholder="Copy from the Buyers/Shops page"
                           onChange={(e) => setForm({...form, ownerId: e.target.value})} />
                </div>
                <div>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Owner display name</div>
                    <Input value={form.ownerName} placeholder="For your own reference in this list"
                           onChange={(e) => setForm({...form, ownerName: e.target.value})} />
                </div>
                <div>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Signup target (optional)</div>
                    <InputNumber style={{width: '100%'}} min={1} value={form.target}
                                 onChange={(v) => setForm({...form, target: v ?? undefined})} />
                </div>
                <div>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Reward note (optional, your own reminder — e.g. "T-shirt" or "MWK 5,000")</div>
                    <Input value={form.rewardNote} onChange={(e) => setForm({...form, rewardNote: e.target.value})} />
                </div>
            </div>
        </Modal>
    );
}

// ── Signups drawer ────────────────────────────────────────────────────────────

function SignupsDrawer({code, open, onClose}: {code: any; open: boolean; onClose: () => void}) {
    const [snapshot, loading] = useCollection(
        code ? query(collection(db, 'referralSignups'), where('code', '==', code.code)) : null,
    );
    const rows = (snapshot?.docs ?? [])
        .map((d) => d.data())
        .sort((a, b) => (b.signupAt?.toMillis?.() ?? 0) - (a.signupAt?.toMillis?.() ?? 0));

    return (
        <Drawer open={open} onClose={onClose} width={480} title={`Signups — ${code?.code ?? ''}`}>
            {loading ? (
                <div style={{padding: 20, textAlign: 'center', color: '#999'}}>Loading…</div>
            ) : rows.length === 0 ? (
                <div style={{padding: 20, textAlign: 'center', color: '#999'}}>No signups yet.</div>
            ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                    {rows.map((r, i) => (
                        <div key={i} style={{display: 'flex', justifyContent: 'space-between', padding: '8px 12px', border: '1px solid #eee', borderRadius: 8}}>
                            <span style={{fontSize: 13}}>{r.newUserName || r.newUserId}</span>
                            <span style={{fontSize: 12, color: '#aaa'}}>{fmtDate(r.signupAt)}</span>
                        </div>
                    ))}
                </div>
            )}
        </Drawer>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────

const ReferralCodes = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<any | null>(null);
    const [viewingSignups, setViewingSignups] = useState<any | null>(null);
    const toast = useToast();

    const [snapshot, loading] = useCollection(collection(db, 'referralCodes'));
    const rows = (snapshot?.docs ?? [])
        .map((d) => ({...d.data()}))
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));

    const toggleStatus = async (row: any) => {
        try {
            await updateDoc(doc(db, 'referralCodes', row.code), {
                status: row.status === 'active' ? 'inactive' : 'active',
            });
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to update', description: e?.message});
        }
    };

    return (
        <>
            <Layout style={{minHeight: '100vh'}}>
                <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
                <Layout className="site-layout">
                    <Sidebar collapsed={collapsed}/>
                    <Content>
                        <Container className="mt-2">
                            <Card className="my-2 p-3 font-JosefinSans">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                                    <Text h4 size={23} css={{color: "#29327b", fontWeight: "$bold"}}>
                                        Referral Codes
                                    </Text>
                                    <NButton size="sm" color="primary" onClick={() => { setEditing(null); setModalOpen(true); }}>
                                        + New code
                                    </NButton>
                                </div>

                                {loading ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>Loading…</div>
                                ) : rows.length === 0 ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>No referral codes yet.</div>
                                ) : (
                                    <div style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                                        {rows.map((row) => {
                                            const target = row.target as number | null;
                                            const pct = target ? Math.min(100, Math.round((row.signupCount / target) * 100)) : null;
                                            return (
                                                <div key={row.code} style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
                                                    padding: '12px 16px', border: '1px solid #eee', borderRadius: 8, background: '#fff',
                                                }}>
                                                    <div style={{cursor: 'pointer'}} onClick={() => setViewingSignups(row)}>
                                                        <div style={{fontWeight: 700, fontSize: 14}}>{row.code}</div>
                                                        <div style={{fontSize: 12, color: '#888'}}>{row.ownerName || row.ownerId}</div>
                                                        {row.rewardNote && <div style={{fontSize: 11, color: '#aaa'}}>Reward: {row.rewardNote}</div>}
                                                    </div>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                                                        <div style={{textAlign: 'right'}}>
                                                            <div style={{fontSize: 13, fontWeight: 600}}>
                                                                {row.signupCount ?? 0}{target ? ` / ${target}` : ''} signups
                                                            </div>
                                                            {pct !== null && (
                                                                <div style={{width: 100, height: 6, background: '#eee', borderRadius: 3, marginTop: 4}}>
                                                                    <div style={{width: `${pct}%`, height: 6, background: '#22c55e', borderRadius: 3}} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Tag color={row.status === 'active' ? 'green' : 'default'}>{row.status}</Tag>
                                                        <Switch size="small" checked={row.status === 'active'} onChange={() => toggleStatus(row)} />
                                                        <NButton size="xs" bordered onClick={() => { setEditing(row); setModalOpen(true); }}>Edit</NButton>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Card>
                        </Container>
                    </Content>
                </Layout>
            </Layout>

            <CodeFormModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} />
            <SignupsDrawer code={viewingSignups} open={viewingSignups !== null} onClose={() => setViewingSignups(null)} />
        </>
    );
};

export default ReferralCodes;
